/**
 * GlobalSearchBar — top-bar Connected Search input + dropdown.
 *
 * Sprint CW-G TKT-2/3/5. A single component because the input + the
 * results panel share a lot of state (active result, focus, recents,
 * keyboard nav) — splitting them out adds prop-drill without clarity.
 *
 * Behavior:
 *   - Cmd-K / Ctrl-K from anywhere focuses the input.
 *   - Typing < 2 chars: dropdown shows recent searches.
 *   - Typing ≥ 2 chars: debounced (300ms) fan-out search across every
 *     connected CW provider via POST /mod/cw/files/search-live.
 *   - Layout toggle (Panels default | Unified) — persisted to localStorage.
 *   - Click result: internal nav for CM page/site; new-tab open for external
 *     provider URLs. Click "See all results" footer → /modules/cw/files?q=…
 *   - Click outside / Esc / route change → close.
 *   - ARIA combobox pattern; ↑/↓ navigate results, Enter selects.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X, Clock, Loader2, Layout, AlignJustify } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useGlobalSearchPrefs } from './useGlobalSearchPrefs';
import './GlobalSearchBar.css';
import CwPanelGroup from '@/modules/cw/components/results/CwPanelGroup';
import CwUnifiedList from '@/modules/cw/components/results/CwUnifiedList';
import CwFilePreview from '@/modules/cw/components/results/CwFilePreview';
import { useCwFilePreview } from '@/modules/cw/components/results/useCwFilePreview';
import '@/modules/cw/styles/cw.css';
import type { CwSearchHit, CwEntityType } from '@/modules/cw/components/results/CwResultRow';

interface ConnectionItem {
  connection_id: string;
  provider_type: string;
  status: string;
}

interface FileListResponse {
  items: CwSearchHit[];
  total?: number;
}

const PROVIDER_LABELS: Record<string, string> = {
  microsoft365: 'Microsoft 365',
  google_drive: 'Google Drive',
  box: 'Box',
  atlassian: 'Atlassian',
  hubspot: 'HubSpot',
  // CW-SF-M3 TKT-9
  salesforce: 'Salesforce',
  cm: 'Intranet Pages',
};

const ENTITY_GROUP_TITLES: Record<CwEntityType, string> = {
  file: 'Files',
  page: 'Pages',
  site: 'Sites',
  issue: 'Issues',
  deal: 'Deals',
  contact: 'Contacts',
  company: 'Companies',
  ticket: 'Tickets',
  // CW-SF-M3 TKT-9 — Salesforce entity groups
  account: 'Salesforce Accounts',
  lead: 'Salesforce Leads',
  opportunity: 'Salesforce Opportunities',
  case: 'Salesforce Cases',
  task: 'Salesforce Tasks',
  event: 'Salesforce Events',
  // CW-SF-M4 — Tier-3 entities
  knowledge: 'Salesforce Knowledge',
  email: 'Salesforce Emails',
  note: 'Salesforce Notes',
  // CW-SF-M6 TKT-7 — Custom Objects share entity_type='custom';
  // resolveCustomGroupTitle below surfaces the per-CO label via
  // payload._sobject_type.
  custom: 'Salesforce Custom Objects',
};


/**
 * CW-SF-M6 TKT-7 — when ``entity_type === 'custom'``, look at the
 * record's payload._sobject_type to surface the actual SObject label
 * (e.g. "Salesforce Project") instead of the generic group title.
 *
 * Used by the per-provider panel headers when rendering Custom
 * Object hits. Falls back to the default ENTITY_GROUP_TITLES lookup
 * for standard entity types.
 */
export function resolveCustomGroupTitle(
  entity: CwEntityType,
  preview?: { _sobject_type?: unknown } | null,
): string {
  if (entity === 'custom') {
    const sobj = preview && typeof preview._sobject_type === 'string'
      ? preview._sobject_type
      : 'Custom Object';
    // Project__c → "Salesforce Project"; Custom_Asset__c → "Salesforce Custom Asset"
    const label = sobj
      .replace(/__c$/, '')
      .replace(/_/g, ' ');
    return `Salesforce ${label}`;
  }
  return ENTITY_GROUP_TITLES[entity] ?? String(entity);
}

const MIN_QUERY_LEN = 2;
const DEBOUNCE_MS = 300;
const PANELS_PER_GROUP = 5;
const UNIFIED_CAP = 10;

const GlobalSearchBar: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { layout, setLayout, recents, pushRecent, clearRecents } = useGlobalSearchPrefs();
  const previewApi = useCwFilePreview();

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState<CwSearchHit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [connections, setConnections] = useState<ConnectionItem[]>([]);

  // Lazy-fetch connections only when the user actually opens the bar.
  // The endpoint returns the array directly (matches UnifiedFiles' call).
  const ensureConnections = useCallback(async () => {
    if (connections.length > 0) return connections;
    try {
      const data = await apiFetch<ConnectionItem[]>('/mod/cw/connections');
      const arr = Array.isArray(data) ? data : [];
      setConnections(arr);
      return arr;
    } catch {
      return [];
    }
  }, [connections]);

  // Cmd-K / Ctrl-K to focus from anywhere.
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: globalThis.MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Close on route change (don't keep dropdown open after Enter selects a CM page).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < MIN_QUERY_LEN) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const conns = await ensureConnections();
      const activeProviders = conns.filter((c) => c.status === 'connected').map((c) => c.provider_type);
      // Always include the internal CM bucket if the search-live endpoint
      // returns it — fan-out is per-provider, but cm hits can still flow
      // through other connected providers' results today. We still ask
      // every provider in parallel.
      if (activeProviders.length === 0) {
        setHits([]);
        setLoading(false);
        setError('No providers connected — connect a workspace under Connected Workspace.');
        return;
      }
      const responses = await Promise.all(
        activeProviders.map((slug) =>
          apiFetch<FileListResponse>('/mod/cw/files/search-live', {
            method: 'POST',
            body: JSON.stringify({ query: q.trim(), provider: slug }),
          }).catch(() => ({ items: [] as CwSearchHit[] })),
        ),
      );
      const merged = responses.flatMap((r) => r.items || []);
      setHits(merged);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed.');
      setHits([]);
    } finally {
      setLoading(false);
    }
  }, [ensureConnections]);

  // Debounced fire on query change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < MIN_QUERY_LEN) {
      setHits([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void runSearch(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  // Group hits by (provider_type, entity_type) for panels mode.
  const panels = useMemo(() => {
    const groups = new Map<string, { provider: string; entity: CwEntityType; hits: CwSearchHit[] }>();
    for (const h of hits) {
      const key = `${h.provider_type}::${h.entity_type}`;
      let g = groups.get(key);
      if (!g) {
        g = { provider: h.provider_type, entity: h.entity_type, hits: [] };
        groups.set(key, g);
      }
      g.hits.push(h);
    }
    return Array.from(groups.values()).sort((a, b) => b.hits.length - a.hits.length);
  }, [hits]);

  // Linearised list of currently visible option IDs in display order
  // (for ↑/↓ keyboard nav).
  const visibleOptionIds = useMemo(() => {
    const ids: string[] = [];
    if (query.trim().length < MIN_QUERY_LEN) {
      recents.forEach((r) => ids.push(`gs-recent-${r}`));
      return ids;
    }
    if (layout === 'unified') {
      hits.slice(0, UNIFIED_CAP).forEach((h) => ids.push(`gs-u-${h.id}`));
    } else {
      panels.forEach((p) => {
        p.hits.slice(0, PANELS_PER_GROUP).forEach((h) =>
          ids.push(`gs-${p.provider}-${p.entity}-${h.id}`),
        );
      });
    }
    return ids;
  }, [hits, panels, layout, query, recents]);

  const selectHit = useCallback((hit: CwSearchHit, _e: MouseEvent | null) => {
    pushRecent(query);
    setOpen(false);
    if (hit.provider_type === 'cm') {
      if (hit.entity_type === 'site') {
        navigate(`/modules/cm/sites/${hit.external_file_id}`);
      } else if (hit.entity_type === 'page') {
        navigate(`/modules/cm/pages/${hit.external_file_id}`);
      } else if (hit.web_url) {
        navigate(hit.web_url);
      }
      return;
    }
    if (hit.web_url) {
      window.open(hit.web_url, '_blank', 'noopener,noreferrer');
    }
  }, [navigate, pushRecent, query]);

  // Connected Search with this provider's panel maximised.
  const openProvider = useCallback((provider: string) => {
    pushRecent(query);
    setOpen(false);
    const params = new URLSearchParams({ q: query.trim(), focus: provider });
    navigate(`/modules/cw/files?${params.toString()}`);
  }, [navigate, pushRecent, query]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (visibleOptionIds.length === 0) return;
      e.preventDefault();
      const idx = activeId ? visibleOptionIds.indexOf(activeId) : -1;
      const nextIdx = e.key === 'ArrowDown'
        ? Math.min(visibleOptionIds.length - 1, idx + 1)
        : Math.max(0, idx - 1);
      setActiveId(visibleOptionIds[nextIdx] ?? null);
      return;
    }
    if (e.key === 'Enter') {
      if (query.trim().length >= MIN_QUERY_LEN && !activeId) {
        // Enter with no active row → go to full Connected Search.
        pushRecent(query);
        navigate(`/modules/cw/files?q=${encodeURIComponent(query.trim())}`);
        setOpen(false);
        return;
      }
      if (activeId) {
        // Walk hits to find the active one and select it.
        const found = hits.find((h) => visibleOptionIds.find((id) => id.endsWith(`-${h.id}`)) === activeId);
        if (found) selectHit(found, null);
        else if (activeId.startsWith('gs-recent-')) {
          const recent = activeId.replace('gs-recent-', '');
          setQuery(recent);
        }
      }
    }
  };

  const showDropdown = open && (query.trim().length >= MIN_QUERY_LEN || recents.length > 0);
  const isRecentsMode = query.trim().length < MIN_QUERY_LEN;

  return (
    <div className="cw-global-search" ref={wrapperRef}>
      <div className="cw-global-search__input-wrap">
        <Search size={16} className="cw-global-search__icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="cw-global-search-listbox"
          aria-activedescendant={activeId ?? undefined}
          aria-autocomplete="list"
          className="cw-global-search__input"
          placeholder="Search files, issues, pages, sites…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveId(null);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {query && (
          <button
            type="button"
            className="cw-global-search__clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              setHits([]);
              setActiveId(null);
              inputRef.current?.focus();
            }}
          >
            <X size={14} />
          </button>
        )}
        <kbd className="cw-global-search__kbd" aria-hidden="true">⌘K</kbd>
      </div>

      {showDropdown && (
        <div
          id="cw-global-search-listbox"
          role="listbox"
          className="cw-global-search__dropdown"
        >
          {/* Header: layout toggle (only when results mode). */}
          {!isRecentsMode && (
            <div className="cw-global-search__head">
              <div className="cw-global-search__layout-toggle" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={layout === 'panels'}
                  className={`cw-global-search__layout-btn${layout === 'panels' ? ' is-active' : ''}`}
                  onClick={() => setLayout('panels')}
                  title="Panels — grouped by provider + type"
                >
                  <Layout size={12} /> Panels
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={layout === 'unified'}
                  className={`cw-global-search__layout-btn${layout === 'unified' ? ' is-active' : ''}`}
                  onClick={() => setLayout('unified')}
                  title="Unified — flat list"
                >
                  <AlignJustify size={12} /> Unified
                </button>
              </div>
            </div>
          )}

          {/* Recents mode (empty query). */}
          {isRecentsMode && (
            <div className="cw-global-search__recents">
              <div className="cw-global-search__recents-head">
                <Clock size={12} />
                <span>Recent searches</span>
                {recents.length > 0 && (
                  <button
                    type="button"
                    className="cw-global-search__clear-recents"
                    onClick={clearRecents}
                  >
                    Clear
                  </button>
                )}
              </div>
              {recents.length === 0 ? (
                <p className="cw-global-search__hint">
                  Search across Microsoft 365, Google Drive, Box, Atlassian, HubSpot, and your intranet pages.
                </p>
              ) : (
                <ul className="cw-global-search__recent-list" role="presentation">
                  {recents.map((r) => {
                    const optId = `gs-recent-${r}`;
                    return (
                      <li key={r} role="presentation">
                        <button
                          type="button"
                          role="option"
                          id={optId}
                          aria-selected={activeId === optId}
                          className={`cw-global-search__recent-row${activeId === optId ? ' is-active' : ''}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setQuery(r);
                            inputRef.current?.focus();
                          }}
                        >
                          <Clock size={12} />
                          <span>{r}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Loading. */}
          {!isRecentsMode && loading && (
            <div className="cw-global-search__loading">
              <Loader2 size={14} className="dm-spin" /> Searching connected workspaces…
            </div>
          )}

          {/* Error. */}
          {!isRecentsMode && !loading && error && (
            <div className="cw-global-search__error">{error}</div>
          )}

          {/* Empty. */}
          {!isRecentsMode && !loading && !error && hits.length === 0 && (
            <div className="cw-global-search__empty">
              No matches for <strong>{query.trim()}</strong>.
            </div>
          )}

          {/* Results — Panels. */}
          {!isRecentsMode && !loading && !error && hits.length > 0 && layout === 'panels' && (
            <div className="cw-global-search__panels">
              {panels.map((p) => (
                <CwPanelGroup
                  key={`${p.provider}-${p.entity}`}
                  title={ENTITY_GROUP_TITLES[p.entity] ?? p.entity}
                  subtitle={PROVIDER_LABELS[p.provider] ?? p.provider}
                  hits={p.hits}
                  limit={PANELS_PER_GROUP}
                  activeId={activeId}
                  idPrefix={`gs-${p.provider}-${p.entity}`}
                  onSelect={selectHit}
                  onOpenProvider={() => openProvider(p.provider)}
                  onHoverEnter={previewApi.onMouseEnter}
                  onHoverMove={previewApi.onMouseMove}
                  onHoverLeave={previewApi.onMouseLeave}
                />
              ))}
            </div>
          )}

          {/* Results — Unified. */}
          {!isRecentsMode && !loading && !error && hits.length > 0 && layout === 'unified' && (
            <CwUnifiedList
              hits={hits}
              limit={UNIFIED_CAP}
              activeId={activeId}
              idPrefix="gs-u"
              onSelect={selectHit}
              onHoverEnter={previewApi.onMouseEnter}
              onHoverMove={previewApi.onMouseMove}
              onHoverLeave={previewApi.onMouseLeave}
            />
          )}

          {/* Footer: see-all. */}
          {!isRecentsMode && query.trim().length >= MIN_QUERY_LEN && (
            <div className="cw-global-search__footer">
              <button
                type="button"
                className="cw-global-search__see-all"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  pushRecent(query);
                  setOpen(false);
                  navigate(`/modules/cw/files?q=${encodeURIComponent(query.trim())}`);
                }}
              >
                See all results in Connected Search →
              </button>
            </div>
          )}
        </div>
      )}
      <CwFilePreview state={previewApi.preview} />
    </div>
  );
};

export default GlobalSearchBar;
